import React, { useState } from 'react';
import axios from 'axios';
import config from '../config';

const SimpleJWTTest = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (test, success, data, error = null) => {
    const result = {
      test,
      success,
      data,
      error,
      timestamp: new Date().toISOString()
    };
    setResults(prev => [result, ...prev]);
    return result;
  };

  const runAllTests = async () => {
    setLoading(true);
    setResults([]);
    
    console.log('🧪 Starting JWT Authentication Tests...');
    
    // Test 1: Check token in localStorage
    const token = localStorage.getItem('token');
    const merchant = localStorage.getItem('merchant');
    
    addResult('Local Storage Check', !!token, {
      tokenPresent: !!token,
      tokenLength: token?.length || 0,
      merchantPresent: !!merchant,
      tokenPreview: token ? token.substring(0, 30) + '...' : null
    });
    
    if (!token) {
      addResult('Authentication Status', false, null, 'No token found - please login first');
      setLoading(false);
      return;
    }
    
    // Test 2: Decode token payload (client-side only)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;
      const isExpired = payload.exp < now;
      
      addResult('Token Decode', true, {
        merchantId: payload.merchantId,
        issuedAt: new Date(payload.iat * 1000).toISOString(),
        expiresAt: new Date(payload.exp * 1000).toISOString(),
        isExpired,
        timeUntilExpiry: isExpired ? 'EXPIRED' : `${Math.round((payload.exp - now) / 3600)} hours`
      });
      
      if (isExpired) {
        addResult('Token Validity', false, null, 'Token is expired - please login again');
        setLoading(false);
        return;
      }
    } catch (e) {
      addResult('Token Decode', false, null, 'Invalid token format: ' + e.message);
    }
    
    // Test 3: Test health endpoint (no auth required)
    try {
      const healthResponse = await axios.get(`${config.API_BASE_URL}/health`);
      addResult('Backend Health', true, healthResponse.data);
    } catch (error) {
      addResult('Backend Health', false, null, error.message);
    }
    
    // Test 4: Test inventory endpoint (requires auth)
    try {
      const inventoryResponse = await axios.get(`${config.API_BASE_URL}/inventory`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      addResult('Inventory Access', true, {
        itemCount: inventoryResponse.data.length,
        status: inventoryResponse.status,
        message: 'Successfully accessed protected route'
      });
    } catch (error) {
      const errorData = {
        status: error.response?.status,
        message: error.response?.data?.error || error.message,
        fullError: error.response?.data
      };
      
      if (error.response?.status === 403) {
        errorData.diagnosis = 'JWT_SECRET mismatch - token created with different secret than verification';
        errorData.solution = 'Set JWT_SECRET on Render and login again';
      } else if (error.response?.status === 401) {
        errorData.diagnosis = 'Token not sent or malformed';
        errorData.solution = 'Check Authorization header format';
      }
      
      addResult('Inventory Access', false, errorData, error.message);
    }
    
    // Test 5: Test another protected route
    try {
      const billsResponse = await axios.get(`${config.API_BASE_URL}/bills`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      addResult('Bills Access', true, {
        billCount: billsResponse.data.length,
        status: billsResponse.status
      });
    } catch (error) {
      addResult('Bills Access', false, {
        status: error.response?.status,
        message: error.response?.data?.error || error.message
      }, error.message);
    }
    
    setLoading(false);
    console.log('🏁 JWT Tests completed');
  };

  const clearResults = () => {
    setResults([]);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h2>🧪 JWT Authentication Diagnostic</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={runAllTests}
          disabled={loading}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginRight: '10px',
            fontSize: '16px'
          }}
        >
          {loading ? '🔄 Running Tests...' : '🚀 Run All Tests'}
        </button>
        
        <button
          onClick={clearResults}
          style={{
            padding: '12px 24px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          🗑️ Clear Results
        </button>
      </div>

      {results.length > 0 && (
        <div>
          <h3>Test Results:</h3>
          {results.map((result, index) => (
            <div
              key={index}
              style={{
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: result.success ? '#d4edda' : '#f8d7da',
                border: `2px solid ${result.success ? '#28a745' : '#dc3545'}`,
                borderRadius: '8px'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <h4 style={{ 
                  margin: 0, 
                  color: result.success ? '#155724' : '#721c24' 
                }}>
                  {result.success ? '✅' : '❌'} {result.test}
                </h4>
                <small style={{ color: '#666' }}>
                  {new Date(result.timestamp).toLocaleTimeString()}
                </small>
              </div>
              
              {result.error && (
                <div style={{ 
                  color: '#721c24', 
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}>
                  Error: {result.error}
                </div>
              )}
              
              {result.data && (
                <details>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                    View Details
                  </summary>
                  <pre style={{
                    backgroundColor: '#fff',
                    padding: '10px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    marginTop: '10px',
                    fontSize: '12px'
                  }}>
                    {JSON.stringify(result.data, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px'
      }}>
        <h3 style={{ marginTop: 0 }}>🎯 Quick Diagnosis Guide</h3>
        <ul style={{ textAlign: 'left' }}>
          <li><strong>No Token:</strong> Login first</li>
          <li><strong>Token Expired:</strong> Login again</li>
          <li><strong>403 Invalid Token:</strong> JWT_SECRET mismatch - set JWT_SECRET on Render and login again</li>
          <li><strong>401 Unauthorized:</strong> Token format issue</li>
          <li><strong>500 Server Error:</strong> Backend configuration problem</li>
        </ul>
        
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <strong>🔧 Most Common Fix:</strong>
          <ol>
            <li>Set JWT_SECRET on Render environment variables</li>
            <li>Wait for redeploy (2-3 minutes)</li>
            <li>Logout and clear localStorage</li>
            <li>Login again</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SimpleJWTTest;